import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppForm } from "../../../../../hooks/useForm";
import { cerrarFase, imputarFase, iniciarFase, type DatosFabricando, type Imputacion } from "../../../../../ts/api/API";
import { Constantes, REGEX_UBICACION, REGEX_CONTENEDOR, REGEX_PUESTO } from "../../../../../ts/constantes";
import z from "zod";
import { SelectorComponentes } from "./FormComponentes";
import { Input } from "../../../../../components/ui/input";

interface FormImputarProps {
	readonly datos: DatosFabricando;
	readonly close: () => void;
}
export function FormImputar(props: FormImputarProps) {
	const { datos } = props;

	const form = useAppForm({
		defaultValues: {
			cantProducida: props.datos.cantLanzada - props.datos.cantAcabada,
			fechaImputacion: new Date().toISOString().split("T")[0],
			ubicacionFabricado: Constantes.UBICACION_PERDIDOS,
			consumos: [],
		} satisfies iModelImputacion as iModelImputacion,
		validators: {
			onSubmit: zModelImputacion,
		},
		onSubmitMeta: { alsoCerrar: false },
		onSubmit: async ({ value, meta }) => {
			await imputar(meta.alsoCerrar, value);
			form.reset();
		},
	});

	const imputarMutation = useMutationInputar(datos.idOF, datos.idFase);


	const imputar = (alsoCerrar: boolean, imputacion: iModelImputacion) => {
		const finalImputacion: Imputacion = {
			idOF: datos.idOF,
			idFase: datos.idFase,
			fechaImputacion: Math.trunc(new Date(imputacion.fechaImputacion).getTime() / 1000),
			cantProducida: imputacion.cantProducida,
			ubicacion: null,
			contenedor: null,
			consumos: [],
		};

		if (REGEX_UBICACION.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;
		if (REGEX_CONTENEDOR.test(imputacion.ubicacionFabricado)) finalImputacion.contenedor = Number(imputacion.ubicacionFabricado);
		if (REGEX_PUESTO.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;

		finalImputacion.consumos = imputacion.consumos.map(c => {
			let ubicacion: string | null = null;
			let contenedor: number | null = null;
			if (c.idUbicacion) {
				if (REGEX_UBICACION.test(c.idUbicacion)) ubicacion = c.idUbicacion;
				if (REGEX_CONTENEDOR.test(c.idUbicacion)) contenedor = Number(c.idUbicacion);
				if (REGEX_PUESTO.test(c.idUbicacion)) ubicacion = c.idUbicacion;
			}

			return {
				...c,
				idUbicacion: ubicacion ?? null,
				idContenedor: contenedor ?? null,
			};
		});

		if (isNaN(finalImputacion.cantProducida) || finalImputacion.cantProducida < 0) {
			alert("La cantidad producida no es un número válido");
			return null;
		}
		if (isNaN(finalImputacion.fechaImputacion)) {
			alert("La fecha introducida no es válida");
			return null;
		}
		if (finalImputacion.ubicacion == null && finalImputacion.contenedor == null) {
			alert("La ubicación de salida no es válida");
			return null;
		}

		if (!REGEX_UBICACION.test(imputacion.ubicacionFabricado)
			&& !REGEX_CONTENEDOR.test(imputacion.ubicacionFabricado)
			&& !REGEX_PUESTO.test(imputacion.ubicacionFabricado)) {
			alert("La ubicación de producto terminado no es válida");
			return null;
		}
		if (finalImputacion.consumos.length == 0) {
			alert("No se puede imputar una fase sin consumos");
			return null;
		}

		if (finalImputacion.consumos.some(sinUbicacion)) {
			alert("La ubicación de algún componente no es válida");
			return null;
		}

		if (Constantes.existenLotes &&
			finalImputacion.consumos.some(c => !c.loteArticulo || c.loteArticulo.trim() === "")) {
			alert("Algún componente requiere un lote y no se ha especificado.");
			return null;
		}

		const alsoAbrir = datos.timestampIni == null;
		return imputarMutation.mutateAsync({ imputacion, alsoAbrir, alsoCerrar })
			.then((success) => {
				if (success) props.close();
			});
	};


	return <>
		<div className="modal-body">
			<div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
				<form.AppField name="cantProducida">
					{field => (
						<field.NumberField
							label="Cant. Producida"
							placeholder=""
							min={1}
							autoComplete="fabricated-quantity"
						/>
					)}
				</form.AppField>

				<form.AppField name="fechaImputacion">
					{field => (
						<field.DateField
							label="Fecha Imputación"
							placeholder=""
							min={1}
							autoComplete="fabricated-quantity"
						/>
					)}
				</form.AppField>

				{Constantes.existenFases ? <>
					<Input label="Fase" readOnly disabled value={datos.descFase} />
					<Input label="Siguiente Fase" readOnly disabled value={datos.nextFaseDesc ?? "Fase Final"} />
				</> : null}

				<form.AppField name="ubicacionFabricado">
					{field => (
						<field.TextField
							label="Ubicacion Prod. Terminado"
							placeholder=""
							// pattern="(^[A-Z]{1}\d{2}[A-Z]{1}\d{2}$)|(^P-\d{2}$)|(^\d+$)"
							autoComplete="fabricated-quantity"
							disabled={Constantes.articulosDesubicados}
						/>
					)}
				</form.AppField>

				<form.AppField name="consumos">
					{field => (
						<div style={{ gridColumn: "1 / -1" }}>
							<h3>Componentes</h3>
							<form.Subscribe selector={state => state.values.cantProducida}>
								{cantProducida => (
									<SelectorComponentes
										cantidadFabricada={cantProducida}
										componentesOF={datos.componentes}
										setConsumos={field.setValue}
									/>
								)}
							</form.Subscribe>
						</div>
					)}
				</form.AppField>
			</div>
		</div>
		<div className="modal-footer">
			<button type="button" className="btnSecondary" onClick={props.close}>Cancelar</button>
			<button type="button" className="btnPrimary" onClick={() => void form.handleSubmit({ alsoCerrar: false })}>Imputar</button>
			<button type="button" className="btnSecondary" onClick={() => void form.handleSubmit({ alsoCerrar: true })}>Imputar y cerrar</button>
		</div>
	</>;
}


export const zConsumo = z.object({
	idArticulo: z.union([z.string(), z.number()]),
	faseArticulo: z.number(),
	loteArticulo: z.string(),

	idUbicacion: z.string(),

	cantidadUsada: z.number(),
	cantidadDefectuosa: z.number(),
	cantidadDesperdiciada: z.number(),
});
export const zModelImputacion = z.object({
	cantProducida: z.number().min(1),
	fechaImputacion: z.iso.date(),
	ubicacionFabricado: z.string(),
	consumos: z.array(zConsumo),
});
export type iModelImputacion = z.infer<typeof zModelImputacion>;
export type iConsumo = z.infer<typeof zConsumo>;

export type ConsumoOrNot = iConsumo | {
	idArticulo: string | number;
	faseArticulo: number;

	idUbicacion?: null;
	idContenedor?: null;
	loteArticulo?: null;

	cantidadUsada?: null;
	cantidadDefectuosa?: null;
	cantidadDesperdiciada?: null;
};

function sinUbicacion(consumo: Imputacion["consumos"][number]) {
	return consumo.idUbicacion == null && consumo.idContenedor == null;
}


async function InputarAsync(imputacion: Imputacion, alsoIniciar: boolean, alsoCerrar: boolean) {
	if (alsoIniciar) {
		const result = await iniciarFase({
			idOF: imputacion.idOF,
			idFase: imputacion.idFase,
			fechaInicio: imputacion.fechaImputacion,
		});

		if (result.status == "error") {
			alert(result.message);
			return false;
		}
	}

	const result = await imputarFase(imputacion);
	if (result.status == "error") {
		alert(result.message);
		return false;
	}

	if (alsoCerrar) {
		const result = await cerrarFase({
			idOF: imputacion.idOF,
			idFase: imputacion.idFase,
			fechaCierre: imputacion.fechaImputacion,
		});

		if (result.status == "error") {
			alert(result.message);
			return false;
		}
	}
	return true;
}

function useMutationInputar(idOF: number, idFase: number) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ imputacion, alsoAbrir, alsoCerrar }: { imputacion: iModelImputacion, alsoAbrir: boolean, alsoCerrar: boolean; }) => {
			const finalImputacion: Imputacion = {
				idOF: idOF,
				idFase: idFase,
				fechaImputacion: Math.trunc(new Date(imputacion.fechaImputacion).getTime() / 1000),
				cantProducida: imputacion.cantProducida,
				ubicacion: null,
				contenedor: null,
				consumos: [],
			};

			if (REGEX_UBICACION.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;
			if (REGEX_CONTENEDOR.test(imputacion.ubicacionFabricado)) finalImputacion.contenedor = Number(imputacion.ubicacionFabricado);
			if (REGEX_PUESTO.test(imputacion.ubicacionFabricado)) finalImputacion.ubicacion = imputacion.ubicacionFabricado;

			finalImputacion.consumos = imputacion.consumos.map(c => {
				let ubicacion: string | null = null;
				let contenedor: number | null = null;
				if (c.idUbicacion) {
					if (REGEX_UBICACION.test(c.idUbicacion)) ubicacion = c.idUbicacion;
					else if (REGEX_CONTENEDOR.test(c.idUbicacion)) contenedor = Number(c.idUbicacion);
					else if (REGEX_PUESTO.test(c.idUbicacion)) ubicacion = c.idUbicacion;
				}

				return {
					...c,
					idUbicacion: ubicacion ?? null,
					idContenedor: contenedor ?? null,
				};
			});

			if (isNaN(finalImputacion.cantProducida) || finalImputacion.cantProducida < 0) {
				return Promise.reject(new Error("La cantidad producida no es un número válido"));
			}

			return InputarAsync(finalImputacion, alsoAbrir, alsoCerrar);
		},
		onSuccess: (resultado) => {
			if (!resultado) return false;

			void queryClient.invalidateQueries({ queryKey: ["getFasesOF", idOF] });
			void queryClient.invalidateQueries({ queryKey: ["imputacionesFase", idOF, idFase] });
			void queryClient.invalidateQueries({ queryKey: ["getOFsEnCurso"] });

			return true;
		},
		onError: (error) => {
			console.error("Error imputando:", error);
			alert(error instanceof Error ? error.message : "Error al imputar");
		},
	});
}